import type { ReactNode } from "react";
import { Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface TableToolbarProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: ReactNode;
  filtersCount?: number;
  filterLabel?: string;
  filterTitle?: string;
  onResetFilters?: () => void;
  className?: string;
}

export function TableToolbar({
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Поиск...",
  filters,
  filtersCount = 0,
  filterLabel = "Фильтр",
  filterTitle = "Фильтры",
  onResetFilters,
  className,
}: TableToolbarProps) {
  const hasSearch = Boolean(onSearchChange);
  const hasFilters = Boolean(filters);

  if (!hasSearch && !hasFilters) {
    return null;
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      {hasSearch && (
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="pl-9"
          />
        </div>
      )}

      {hasFilters && (
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              <span>{filterLabel}</span>
              {filtersCount > 0 && (
                <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-medium text-primary">
                  {filtersCount}
                </span>
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>{filterTitle}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4">{filters}</div>
            <DialogFooter>
              {onResetFilters && (
                <Button variant="outline" onClick={onResetFilters}>
                  Сбросить
                </Button>
              )}
              <DialogClose asChild>
                <Button>Готово</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
